import type { Config } from 'driver.js'
import type { OnboardingStep } from './types'

function syncProgressLabelToFrench(): void {
  if (!import.meta.client) {
    return
  }
  const popover = document.querySelector('.driver-popover')
  const progressNode = popover?.querySelector('.driver-popover-progress-text')
  if (!(progressNode instanceof HTMLElement)) {
    return
  }
  const rawText = progressNode.textContent ?? ''
  const normalizedText = rawText.replace(/\s+of\s+/i, ' sur ')
  if (normalizedText !== rawText) {
    progressNode.textContent = normalizedText
  }
}

/**
 * Construit la configuration Driver.js pour un tour donné.
 * Toute la logique métier (progression, verrouillage, navigation) est déléguée
 * aux callbacks fournis par `useOnboardingDriverRuntime` : cette fonction ne fait
 * que traduire les évènements Driver.js vers ces callbacks.
 */
type DriverConfigControls = {
  onRequestStepTeleport: (targetIndex: number) => void
  onSyncNextButton: (stepIndex: number) => void
  isStrictFirstRun: boolean
  isToolboxCurrentlyOpen: () => boolean
  stepsCount: number
  onStepChange: (stepIndex: number) => void
  onClose: (isCompleted: boolean, stepIndex: number) => void
}

export function createDriverConfig(
  routePathGetter: () => string,
  sourceSteps: OnboardingStep[],
  onRequireToolboxOpen: (required: boolean) => void,
  onInvalidPathStep: (message: string) => void,
  controls: DriverConfigControls
): Config {
  let maxReachedStepIndex = 0

  function rememberStepIndex(stepIndex: number): void {
    if (stepIndex > maxReachedStepIndex) {
      maxReachedStepIndex = stepIndex
    }
  }

  return {
    steps: [],
    animate: true,
    smoothScroll: true,
    showProgress: true,
    allowClose: false,
    popoverClass: 'monorga-driver-popover',
    nextBtnText: 'Suivant',
    prevBtnText: 'Précédent',
    doneBtnText: 'Terminer',
    overlayClickBehavior: () => {},
    // Ne pas couper l’interaction sur l’élément actif : on pilote le verrouillage par click-lock (PDL only).
    disableActiveInteraction: false,
    allowKeyboardControl: false,
    stagePadding: 10,
    stageRadius: 10,
    onHighlighted: function onHighlighted(_element, _step, ctx) {
      const activeIndex = ctx.state.activeIndex ?? 0
      rememberStepIndex(activeIndex)
      const sourceStep = sourceSteps[activeIndex]
      if (sourceStep?.openToolbox) {
        onRequireToolboxOpen(true)
      }
      controls.onSyncNextButton(activeIndex)
      syncProgressLabelToFrench()
      controls.onStepChange(activeIndex)
    },
    onNextClick: function onNextClick(_element, _step, ctx) {
      const activeIndex = ctx.state.activeIndex ?? 0
      const sourceStep = sourceSteps[activeIndex]
      if (controls.isStrictFirstRun && sourceStep?.openToolbox && !controls.isToolboxCurrentlyOpen()) {
        onInvalidPathStep('Ouvrez la toolbox pour continuer le tutoriel.')
        return
      }
      if (
        sourceStep?.requiredPathPrefix &&
        sourceStep.requiredPathPrefix.length > 0 &&
        !routePathGetter().startsWith(sourceStep.requiredPathPrefix)
      ) {
        onInvalidPathStep(
          sourceStep.requiredPathMessage ??
            'Veuillez ouvrir la page demandee avant de continuer le tutoriel.'
        )
        return
      }
      ctx.driver.moveNext()
    },
    onPrevClick: function onPrevClick(_element, _step, ctx) {
      const activeIndex = ctx.state.activeIndex ?? 0
      if (activeIndex <= 0) {
        return
      }
      controls.onRequestStepTeleport(activeIndex - 1)
      // Le déplacement est piloté par `useOnboarding` après navigation/DOM prêt.
    },
    onDestroyed: function onDestroyed(_element, _step, ctx) {
      const activeIndex = ctx.state.activeIndex ?? 0
      rememberStepIndex(activeIndex)
      const isCompleted = maxReachedStepIndex >= controls.stepsCount - 1
      controls.onClose(isCompleted, maxReachedStepIndex)
    },
  }
}
